import type { CampaignTypes } from 'features/campaigns';
import type IGameSettings from 'types/models/IGameSettings';
import { SEARCH_FEATURES_OPTIONS, SEARCH_FILTERS } from '../constants/searchConstants';
import type { IFilterOption } from '../types/filter.types';
import { categorizeGame, getNormalizedGameFeatures, normalizeString } from './gameSearchHelpers';

/**
 * Helper function for normalizing filter option IDs
 */
const normalizeFilterOptionId = (id: string): string => normalizeString(id.replace(/_/g, ' '));

/**
 * Functions to generate filter options based on the available games data
 */
export const createFilterLinesOptions = (games: IGameSettings[]): IFilterOption[] => {
  const uniqueSortedLines = Array.from(new Set(games.flatMap((game) => game.lines ?? []))).sort((a, b) => a - b);

  // Exclude the option for games with 0 lines
  return uniqueSortedLines
    .filter((line) => line !== 0)
    .map((line) => ({
      id: line.toString(),
      label: line.toString(),
    }));
};

export const filterFeatureOptionsByAvailability = (
  games: IGameSettings[],
  featureOptions: IFilterOption[],
  hasHideBuyBonusRestriction: boolean,
): IFilterOption[] => {
  const availableFeatures = games.reduce<Set<string>>((features, game) => {
    getNormalizedGameFeatures(game.gameFeatures).forEach((feature) => {
      if (hasHideBuyBonusRestriction && feature === SEARCH_FEATURES_OPTIONS.BUY_BONUS) {
        return;
      }

      features.add(feature);
    });

    return features;
  }, new Set());

  return featureOptions.filter((option) => availableFeatures.has(normalizeString(option.id)));
};

export const filterMaxWinOptionsByAvailability = (
  games: IGameSettings[],
  maxWinOptions: IFilterOption[],
): IFilterOption[] => {
  const availableMaxWins = new Set(
    games
      .map((game) => categorizeGame(game, SEARCH_FILTERS.MAX_WIN))
      .filter((value): value is string => value !== null),
  );

  return maxWinOptions.filter((option) => availableMaxWins.has(option.id));
};

export const filterPayoutOptionsByAvailability = (
  games: IGameSettings[],
  payoutOptions: IFilterOption[],
): IFilterOption[] => {
  const availablePayouts = new Set(games.map((game) => game.paymode && normalizeString(game.paymode)));

  return payoutOptions.filter((option) => {
    const normalizedOptionId = normalizeFilterOptionId(option.id);
    return availablePayouts.has(normalizedOptionId);
  });
};

export const filterJackpotOptionsByAvailability = (
  games: IGameSettings[],
  jackpotOptions: IFilterOption[],
): IFilterOption[] => {
  const availableJackpots = new Set(
    games
      .map((game) => categorizeGame(game, SEARCH_FILTERS.JACKPOT))
      .filter((value): value is string => value !== null)
      .map(normalizeString),
  );

  return jackpotOptions.filter((option) => {
    const normalizedOptionId = normalizeFilterOptionId(option.id);
    return availableJackpots.has(normalizedOptionId);
  });
};

export const filterCampaignOptionsByAvailability = (
  filter: { options: IFilterOption[] },
  visibleCampaignIds: Set<CampaignTypes>,
): { options: IFilterOption[] } | null => {
  // Hide the entire filter if no campaign options are available
  if (visibleCampaignIds.size === 0) {
    return null;
  }

  return {
    ...filter,
    options: filter.options.filter((option) => {
      return visibleCampaignIds.has(option.id as CampaignTypes);
    }),
  };
};
